<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { SizeOptions } from '@floating-ui/vue';
import { IconChevronDown, IconCheck } from '@/icons';
import { ObHostedDropdown } from '../hosted-dropdown';
type Option = {
  label: string;
  value: string;
  description?: string;
};
interface Props {
  modelValue?: string | null;
  options?: Option[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  label: '',
  placeholder: 'Select',
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();
const open = ref(false);
const selectedLabel = computed(() => {
  const found = props.options.find((o) => o.value === props.modelValue);
  return found?.label ?? null;
});
const hasValue = computed(() => !!selectedLabel.value);
const matchWidth: SizeOptions = {
  apply({ rects, elements }) {
    Object.assign(elements.floating.style, {
      width: `${rects.reference.width}px`,
    });
  },
};
watch(open, (value) => {
  if (value) {
    window.addEventListener('scroll', closeOnScroll, true);
  } else {
    window.removeEventListener('scroll', closeOnScroll, true);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', closeOnScroll, true);
});
function closeOnScroll() {
  open.value = false;
}
function onHostClick() {
  if (!props.disabled) {
    open.value = !open.value;
  }
}
function selectOption(option: Option) {
  emit('update:modelValue', option.value);
  open.value = false;
}
</script>

<template>
  <div :class="$style.root">
    <div v-if="props.label" :class="$style.label">{{ props.label }}</div>
    <ObHostedDropdown
      v-model:active="open"
      tabindex="-1"
      shift
      flip
      :padding="4"
      :size="matchWidth"
      no-scale-transition
      @click-outside="open = false"
      @keydown.esc.prevent="open = false"
    >
      <template #host="{ hostProps }">
        <button
          v-bind="hostProps"
          type="button"
          data-ui-component="UiSelectField.trigger"
          :disabled="props.disabled"
          :class="[
            $style.trigger,
            {
              [$style.hasValue]: hasValue,
              [$style.open]: open,
              [$style.disabled]: props.disabled,
            },
          ]"
          @click="onHostClick"
        >
          <span :class="[$style.triggerText, { [$style.triggerPlaceholder]: !hasValue }]">
            {{ selectedLabel ?? props.placeholder }}
          </span>
          <IconChevronDown
            :size="18"
            :class="[$style.chevron, { [$style.chevronOpen]: open }]"
            aria-hidden="true"
          />
        </button>
      </template>

      <div :class="$style.dropdown">
        <button
          v-for="option in props.options"
          :key="option.value"
          type="button"
          :class="[$style.item, { [$style.itemSelected]: props.modelValue === option.value }]"
          @click="selectOption(option)"
        >
          <span :class="$style.itemCopy">
            <span :class="$style.itemLabel">{{ option.label }}</span>
            <span v-if="option.description" :class="$style.itemDescription">
              {{ option.description }}
            </span>
          </span>
          <IconCheck
            v-if="props.modelValue === option.value"
            :size="16"
            :class="$style.itemCheck"
          />
        </button>
      </div>
    </ObHostedDropdown>
  </div>
</template>

<style lang="scss" module>
@use '@/ui-kit/styles/typography';

.root {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.label {
  font-family: typography.$font-family-primary;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  font-style: normal;
  color: var(--ui2-content-secondary, #655e60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trigger {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1.5px solid transparent;
  background: var(--ui2-shade-black-4, rgba(0, 0, 0, 0.04));
  cursor: pointer;

  &:hover {
    background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));
  }

  &.hasValue {
    background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));

    &:hover {
      background: var(--ui2-shade-black-12, rgba(0, 0, 0, 0.12));
    }
  }

  &.open {
    background: var(--ui2-background-primary, #fff);
    border-color: var(--ui2-state-focus, rgba(51, 85, 255, 1));

    &:hover,
    &.hasValue:hover {
      background: var(--ui2-background-primary, #fff);
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.48;
    pointer-events: none;
  }
}

.triggerText {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  font-weight: 400;
  font-style: normal;
  color: var(--ui2-content-primary, #010000);
}

.triggerPlaceholder {
  color: var(--ui2-content-tertiary, #978f91);
}

.chevron {
  flex-shrink: 0;
  color: var(--ui2-content-primary, #010000);
  transition: transform 0.15s ease;
}

.chevronOpen {
  transform: rotate(180deg);
}

.dropdown {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  box-sizing: border-box;
}

.item {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: var(--ui2-surface-secondary, #f5f5f5);
  }
}

.itemCopy {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.itemLabel {
  font-family: typography.$font-family-primary;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ui2-content-primary, #010000);
}

.itemDescription {
  font-family: typography.$font-family-primary;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: var(--ui2-content-secondary, #655e60);
}

.itemSelected .itemLabel {
  color: var(--ui2-background-accent-primary, #226b6c);
}

.itemCheck {
  flex-shrink: 0;
  color: var(--ui2-background-accent-primary, #226b6c);
}
</style>
